import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../services/api';

export interface CurrentUser {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  profilePicture: string;
}

// 권한 타입 정의
export type UserRole = 'ADMIN' | 'EMPLOYEE' | 'DEVELOPER' | 'PROJECT_MANAGER';

// 페이지 권한 정의
export interface PagePermission {
  path: string;
  name?: string;
  allowedRoles?: UserRole[];
}


interface AuthContextType {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  userRole: UserRole | null;
  accessiblePaths: string[];
  logout: () => void;
  checkAuth: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  hasPageAccess: (path: string) => boolean;
  getAccessiblePages: () => PagePermission[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [accessiblePages, setAccessiblePages] = useState<PagePermission[]>([]);
  const [accessiblePaths, setAccessiblePaths] = useState<string[]>([]);

  // 백엔드에서 접근 가능한 페이지 목록 가져오기
  const fetchAccessiblePages = async (userId: number) => {
    try {
      const pages = await apiClient.get<PagePermission[]>(`/permissions/accessible/${userId}`);
      if (Array.isArray(pages)) {
        setAccessiblePages(pages);
        setAccessiblePaths(pages.map((p: PagePermission) => p.path));
      }
    } catch (error) {
      console.error('Failed to fetch accessible pages:', error);
    }
  };

  // 권한 새로고침 (권한 변경 후 호출)
  const refreshPermissions = async () => {
    if (user?.id) {
      await fetchAccessiblePages(user.id);
    }
  };

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('accessToken');

      if (token) {
        try {
          // apiClient를 통해 /auth/me 호출 (401 시 api.ts가 자동 refresh 처리)
          const userData = await apiClient.get<any>('/auth/me');

          setUser({
            id: userData.id,
            email: userData.email,
            name: userData.name,
            role: userData.role,
            profilePicture: userData.profilePicture || '',
          });
          setIsAuthenticated(true);
          setUserRole(userData.role);

          // 접근 가능한 페이지 목록 가져오기
          await fetchAccessiblePages(userData.id);
        } catch (error) {
          logout();
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setUserRole(null);
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      setUserRole(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);


  // 권한 관련 함수들
  const hasPageAccess = (path: string): boolean => {
    if (!userRole) return false;

    // 백엔드에서 가져온 접근 가능 페이지 목록 확인
    if (accessiblePaths.length > 0) {
      return accessiblePaths.includes(path);
    }

    // 권한 설정이 없는 페이지는 접근 허용
    return true;
  };

  const getAccessiblePages = (): PagePermission[] => {
    return accessiblePages;
  };


  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setUserRole(null);
    setAccessiblePages([]);
    setAccessiblePaths([]);

    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        userRole,
        accessiblePaths,
        logout,
        checkAuth,
        refreshPermissions,
        hasPageAccess,
        getAccessiblePages,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
